import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { createElement, type CSSProperties } from "react";
import { ptPT as corePtPT } from "@mui/material/locale";
import { createTheme, keyframes } from "@mui/material/styles";
import { ptPT as pickersPtPT } from "@mui/x-date-pickers/locales";
// Registers the picker component keys (MuiPickerPopper) on ThemeOptions.
import type {} from "@mui/x-date-pickers/themeAugmentation";

export const palette = {
  primary: {
    50: "#E7F5F1",
    100: "#CEEBE4",
    200: "#B6E1D6",
    300: "#6CC3AD",
    400: "#3BAF91",
    500: "#0A9B76",
    base: "#0A9B76",
    600: "#087C5E",
    700: "#065D47",
    800: "#043E2F",
    900: "#032F23",
    950: "#01100C",
  },
  info: "#1667AD",
  infoLight: "#E1EAF2",
  success: "#2F9741",
  /** Darkened step for text on light fills: #2F9741 is ~3.9:1 on white. */
  successDark: "#1B5E28",
  successLight: "#E4EFE7",
  warning: "#D08700",
  warningDark: "#93650F",
  warningLight: "#F4EEE1",
  error: "#AB2424",
  errorDark: "#810909",
  errorLight: "#F0E4E5",
  blue: "#3A95FF",
  /** A hue no other status uses, for a skipped communication delivery. */
  violet: "#7048C4",
  violetDark: "#4E2E91",
  violetLight: "#EFEAF8",
  neutral: {
    0: "#FFFFFF",
    5: "#F5F5F5",
    10: "#EBEBEB",
    20: "#D5D5D5",
    30: "#BCBCBC",
    40: "#9FA0A0",
    50: "#7C7D7D",
    base: "#7C7D7D",
    60: "#6F7070",
    70: "#606161",
    80: "#4E4F4F",
    90: "#373838",
    95: "#272828",
    100: "#000000",
  },
};

/**
 * Corner radius scale. `md` (8) is also set as `shape.borderRadius`, so it is
 * what every MUI component falls back to when no radius is given.
 *
 * Prefer these over literals: a bare number means px inside `styled()` but a
 * `shape.borderRadius` multiplier inside `sx`, and that ambiguity is what let
 * ~20 different radii accumulate across the app.
 */
export const radius = {
  none: "0",
  sm: "4px",
  md: "8px",
  lg: "16px",
  pill: "999px",
} as const;

/**
 * Motion roles for short UI transitions. Structural entry motion is limited to
 * opacity and transform so it stays on the compositor and does not change
 * document geometry while an embedded host is measuring the app. Feedback
 * roles may animate the explicit visual properties changed by an interaction.
 */
export const motion = {
  duration: {
    feedback: 120,
    calendarFeedback: 90,
    press: 180,
    content: 220,
    page: 240,
    disclosureEnter: 320,
    disclosureExit: 240,
    dialogEnter: 240,
    dialogExit: 180,
    loadingFade: 140,
  },
  delay: {
    loading: 120,
  },
  distance: {
    page: "4px",
    step: "6px",
    dialog: "4px",
  },
  scale: {
    dialog: 0.985,
  },
  easing: {
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",
    enter: "cubic-bezier(0, 0, 0.2, 1)",
    exit: "cubic-bezier(0.4, 0, 1, 1)",
  },
} as const;

const dialogEnter = keyframes({
  from: {
    transform: `translateY(${motion.distance.dialog}) scale(${motion.scale.dialog})`,
  },
  to: {
    transform: "translateY(0) scale(1)",
  },
});

/**
 * Elevation scale. Only three steps on purpose — `card` for resting surfaces,
 * `raised` for hover/floating, `none` to opt out. State-carrying shadows
 * (focus rings, drag affordances) are not elevation and stay local.
 */
export const elevation = {
  none: "none",
  /** Resting surface: cards, popovers, calendar panels. */
  card: "0px 1px 3px 0px #4A4A4A40",
  /** Interactive resting surface: buttons, inputs, date pickers. */
  raised: "0px 1px 4px 0px #4A4A4A40",
  /** Brand-tinted lift on hover. */
  hover: `0 2px 8px ${palette.primary.base}1A`,
  /** An element being dragged. */
  dragging: "0 4px 12px rgba(0, 0, 0, 0.2)",
  /**
   * Picked up off the surface behind it. Two layers on purpose: the tight one
   * anchors the edge, the wide soft one is what actually reads as height. Used
   * where a scale or a translate would disturb a dense layout.
   */
  lifted:
    "0 10px 24px -6px rgba(0, 0, 0, 0.28), 0 3px 8px -2px rgba(0, 0, 0, 0.18)",
  /** Resize/focus ring. */
  focus: `0 0 0 2px ${palette.blue}80`,
} as const;

/**
 * Hairline borders. `default` outlines a surface or control; `subtle` is the
 * lighter internal rule between rows inside one surface.
 */
export const border = {
  default: `1px solid ${palette.neutral[20]}`,
  subtle: `1px solid ${palette.neutral[10]}`,
  /** The stronger rule used by the calendar grid and its column headers. */
  strong: `1px solid ${palette.neutral[40]}`,
} as const;

/**
 * Named background roles, so the grey card surface has one source.
 *
 * `padding` is the single inset for every page-level surface and card, and it
 * is deliberately symmetric: pages previously mixed 24x32, 24x48, 20 and 32,
 * which made the gutter differ both between pages and between the horizontal
 * and vertical edges of the same page.
 */
export const surface = {
  base: palette.neutral[0],
  subtle: palette.neutral[10],
  padding: "24px",
  /** Same inset, stepped down for narrow viewports. */
  paddingCompact: "16px",
  /**
   * Inset for a list row inside a surface (client results, pet cards, history
   * items). Deliberately tighter horizontally than the page inset, because a
   * row is already indented by its parent surface.
   */
  paddingRow: "24px 16px",
  /**
   * Space between the viewport edge and the page's surfaces. Applied once, on
   * the app-level wrapper in `App.tsx`, so every route gets it identically —
   * never re-declare it on a route root.
   */
  pageGutter: "16px",
  /** Same gutter, stepped down for narrow viewports. */
  pageGutterCompact: "8px",
  /**
   * Gap between a dropdown/date input and the list it opens, so the popup reads
   * as a separate surface instead of being welded to the field.
   */
  popoverGap: "4px",
} as const;

/**
 * Names of the CSS custom properties that describe how much of the app's own
 * viewport is actually on screen. `useVisibleViewport` is the only writer.
 *
 * The app can ask its host for an iframe as tall as its content. When the host
 * shows only part of that frame, `100vh`, `position: fixed` and every viewport
 * measurement MUI makes describe a box that runs past the visible screen. These
 * properties carry the difference so layers can centre on the visible band.
 */
export const visibleViewportVars = {
  top: "--app-visible-top",
  bottom: "--app-visible-bottom",
  height: "--app-visible-height",
  hostCorner: "--app-host-corner-inset",
  scrollRoom: "--app-scroll-room",
} as const;

/**
 * The same properties as `var()` reads, with the standalone value as fallback —
 * so every rule below behaves exactly as it did before the hook existed until
 * something actually measures a clipped viewport.
 */
export const visibleViewport = {
  /** Hidden strip above the visible band. */
  top: `var(${visibleViewportVars.top}, 0px)`,
  /** Hidden strip below the visible band. */
  bottom: `var(${visibleViewportVars.bottom}, 0px)`,
  /** Height of the visible band. */
  height: `var(${visibleViewportVars.height}, 100vh)`,
  /**
   * Width to keep clear in the top-right corner for a control the host paints
   * over our frame. `0px` unless we are embedded — see the hook.
   */
  hostCorner: `var(${visibleViewportVars.hostCorner}, 0px)`,
  /**
   * Bottom scroll room the app has to add for itself, because it is the only
   * thing on screen that can scroll and the host has left part of the frame
   * outside the visible band.
   *
   * `0px` in every other case, and that is the common one: a host that honours
   * the resize request leaves nothing clipped, and a host page that can scroll
   * reaches the rest of the frame itself.
   */
  scrollRoom: `var(${visibleViewportVars.scrollRoom}, 0px)`,
} as const;

// Extend the theme to include our custom palette and design tokens.
declare module "@mui/material/styles" {
  interface Palette {
    neutral: typeof palette.neutral;
  }

  interface PaletteOptions {
    neutral?: typeof palette.neutral;
  }

  interface TypographyVariants {
    /** Form labels: one step stronger than ordinary body2 text. */
    label: CSSProperties;
    /** Large numeric/statistical value. */
    metric: CSSProperties;
    /** Deliberately compact scheduler text that sits between caption and body2. */
    dense: CSSProperties;
  }

  interface TypographyVariantsOptions {
    label?: CSSProperties;
    metric?: CSSProperties;
    dense?: CSSProperties;
  }
}

declare module "@mui/material/Autocomplete" {
  interface AutocompletePaperSlotPropsOverrides {
    /**
     * Pinned under the options, whether they are loading, listed or none. Only
     * a `paper` slot that renders it shows it, as the sale pickers' does.
     */
    footer?: import("react").ReactNode;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    label: true;
    metric: true;
    dense: true;
  }
}

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    /**
     * The one width at which the desktop layout is allowed to change. Every page
     * keeps the layout it has at ~1400px all the way down to 1024px and only
     * rearranges below it, so the switches that used to fire at `lg` (1200) —
     * and Booking's own 1000px key — all live here instead.
     */
    compact: true;
  }
}

// Create a theme instance.
const theme = createTheme(
  {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        compact: 1024,
        lg: 1200,
        xl: 1536,
      },
    },
    shape: {
      // Numeric because MUI multiplies it for `sx` values; keep in sync with
      // `radius.md`. The `radius` tokens are px strings so they are safe in both
      // `sx` (where a bare number would be multiplied) and `styled()`.
      borderRadius: 8,
    },
    palette: {
      primary: palette.primary,
      secondary: {
        main: "#C5060B",
      },
      error: {
        main: palette.error,
      },
      success: {
        main: "#56C568",
        light: "#6FCF97",
      },
      background: {
        default: palette.neutral[0],
      },
      text: {
        primary: palette.neutral[90],
        secondary: palette.neutral[50],
        disabled: palette.neutral[30],
      },
      neutral: palette.neutral,
    },
    typography: {
      fontFamily: `'Open Sans', 'Helvetica', 'Arial', sans-serif`,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      // Line heights are proportional to font size. They were all pinned at
      // "16px" before, which put h1-body1 at or below their own font size and
      // forced ~180 inline fontSize/lineHeight overrides to compensate.
      h1: {
        fontSize: 32,
        fontWeight: 400,
        lineHeight: "40px",
      },
      h2: {
        fontSize: 28,
        fontWeight: 400,
        lineHeight: "36px",
      },
      h3: {
        fontSize: 24,
        fontWeight: 500,
        lineHeight: "32px",
      },
      h4: {
        fontSize: 20,
        fontWeight: 500,
        lineHeight: "28px",
      },
      h5: {
        fontSize: 18,
        fontWeight: 600,
        lineHeight: "26px",
      },
      h6: {
        fontSize: 16,
        fontWeight: 600,
        lineHeight: "24px",
      },
      body1: {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: "24px",
      },
      body2: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: "20px",
      },
      subtitle1: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: "24px",
      },
      subtitle2: {
        fontSize: 14,
        fontWeight: 600,
        lineHeight: "20px",
      },
      caption: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: "18px",
      },
      overline: {
        fontSize: 10,
        fontWeight: 400,
        lineHeight: "16px",
      },
      button: {
        fontSize: 14,
        fontWeight: 600,
        lineHeight: "20px",
        textTransform: "none",
      },
      label: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: "20px",
      },
      metric: {
        fontSize: 40,
        fontWeight: 600,
        lineHeight: "48px",
      },
      dense: {
        fontSize: 11,
        fontWeight: 600,
        lineHeight: "16px",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            margin: 0,
            padding: 0,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationDelay: "0ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              transitionDelay: "0ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      },
      // Interactive controls all share `radius.sm` — the radius of the "Agendar"
      // button. Surfaces (cards, dialogs, menus) keep their own larger radii;
      // `shape.borderRadius` still covers those, so it is deliberately untouched.
      MuiCollapse: {
        defaultProps: {
          timeout: {
            enter: motion.duration.disclosureEnter,
            exit: motion.duration.disclosureExit,
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          // Accordion supplies `auto` to its Collapse slot internally, so set the
          // slot timeout explicitly to keep large disclosures below 500ms.
          slotProps: {
            transition: {
              timeout: {
                enter: motion.duration.disclosureEnter,
                exit: motion.duration.disclosureExit,
              },
            },
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          // MUI restores focus from FocusTrap (a child of Modal) before Modal
          // clears aria-hidden on #root, so the trigger regains focus while still
          // inside an aria-hidden subtree and Chrome blocks it. useDialogFocusRestore
          // puts focus back once the dialog is fully gone.
          disableRestoreFocus: true,
          transitionDuration: {
            enter: motion.duration.dialogEnter,
            exit: motion.duration.dialogExit,
          },
          slotProps: {
            transition: {
              easing: {
                enter: motion.easing.enter,
                exit: motion.easing.exit,
              },
            },
          },
        },
        styleOverrides: {
          /*
           * A dialog centres itself in the app's viewport with `position:
           * fixed`. When a host frame is taller than the area it actually shows,
           * that centre is off screen — measured against the Loop Hubs embed, a
           * dialog's action row sat 75px below the fold with scrolling locked
           * inside the frame, so Confirmar/Cancelar were unreachable. Trimming
           * the fixed box to the visible band puts the dialog back in the middle
           * of what the user can see. Both values are 0 while the app owns its
           * viewport, which leaves standalone rendering untouched.
           */
          root: {
            top: visibleViewport.top,
            bottom: visibleViewport.bottom,
          },
          /*
           * A dialog is a page surface, so it takes the same 16px corner as
           * `StyledPaper` and the `radius.lg` cards on the pages behind it —
           * not MUI's 8px default. Set once here because the app has 15 of
           * them: most already declared `radius.lg` by hand, while AgendaModal
           * (and the three modals reusing its `StyledDialog`) sat at the 8px
           * default, `ConfirmActionDialog` at 4px, and the raw `Dialog` call
           * sites at whatever they happened to pass. Do not re-declare it on a
           * dialog; change it here.
           */
          paper: {
            borderRadius: radius.lg,
            animation: `${dialogEnter} ${motion.duration.dialogEnter}ms ${motion.easing.enter} backwards`,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ["background-color", "border-color", "color", "opacity"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
            "& .MuiTouchRipple-rippleVisible": {
              animationDuration: `${motion.duration.press}ms`,
            },
            "& .MuiTouchRipple-ripplePulsate": {
              animationDuration: `${motion.duration.press}ms`,
            },
            "& .MuiTouchRipple-childLeaving": {
              animationDuration: `${motion.duration.press}ms`,
            },
          }),
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ["background-color", "color", "opacity"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
            // MUI's checked dot is an internal SVG whose default 150ms timing
            // cannot be configured through a public slot. Scope the duration to
            // Radio descendants instead of changing the global MUI scale.
            "& svg": {
              transitionDuration: `${motion.duration.feedback}ms`,
              transitionTimingFunction: motion.easing.standard,
            },
          }),
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: ({ theme }) => ({
            transition: theme.transitions.create(["left", "transform"], {
              duration: motion.duration.feedback,
              easing: motion.easing.standard,
            }),
          }),
          track: ({ theme }) => ({
            transition: theme.transitions.create(
              ["background-color", "opacity"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.button,
            borderRadius: radius.sm,
            transition: theme.transitions.create(
              ["background-color", "border-color", "box-shadow", "color"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ["background-color", "border-color", "box-shadow", "color"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.label,
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          /*
           * Hint and error text starts at the field's own left edge.
           *
           * MUI's default is `margin: 3px 14px 0`, an inset inherited from the
           * filled variant that has nothing to line up with on an outlined
           * field: it left every helper 14px adrift of the box it describes.
           * The components that draw their own error text already zeroed this
           * by hand; setting it here is what makes the rule uniform.
           */
          root: ({ theme }) => ({
            ...theme.typography.caption,
            marginInline: 0,
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            // Hover reads as a light tint of the brand rather than a grey shift.
            "&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline":
              {
                borderColor: palette.primary[300],
              },
          },
          notchedOutline: {
            borderRadius: radius.sm,
            transition: `border-color ${motion.duration.feedback}ms ${motion.easing.standard}`,
          },
        },
      },
      // MUI X pickers render their own input (MuiPickersOutlinedInput), which the
      // MuiOutlinedInput override above does not reach — that is why the date and
      // time fields kept the 8px shape default while every other input was 4px.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            backgroundColor: surface.base,
            "&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiPickersOutlinedInput-notchedOutline":
              {
                borderColor: palette.primary[300],
              },
          },
          notchedOutline: {
            borderRadius: radius.sm,
            transition: `border-color ${motion.duration.feedback}ms ${motion.easing.standard}`,
          },
        },
      },
      MuiSelect: {
        // One animated expand icon for every Select, including direct MUI uses.
        defaultProps: {
          IconComponent: ExpandMoreIcon,
        },
        styleOverrides: {
          select: {
            borderRadius: radius.sm,
          },
          icon: ({ theme }) => ({
            transition: theme.transitions.create("transform", {
              duration: motion.duration.feedback,
              easing: motion.easing.standard,
            }),
          }),
          iconOpen: {
            transform: "rotate(180deg)",
          },
        },
      },
      // Dropdown lists sit flush against the popover edges — MUI's default 8px
      // block padding on the list made them look loose.
      MuiList: {
        styleOverrides: {
          root: {
            paddingBlock: 0,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ["background-color", "color"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create(
              ["background-color", "color", "opacity"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius.sm,
            transition: theme.transitions.create(
              ["background-color", "border-color", "box-shadow", "color"],
              {
                duration: motion.duration.feedback,
                easing: motion.easing.standard,
              },
            ),
          }),
        },
      },
      // Menu extends Popover, so this one rule gives every Select menu and
      // Popover-based dropdown (including TreeSelect) the same gap below its
      // input. Popovers anchored to a point rather than below a field opt out
      // locally with `marginTop: 0`.
      MuiPopover: {
        styleOverrides: {
          /*
           * A menu paper is `position: absolute` inside this box and sizes itself
           * with `maxHeight: calc(100% - 32px)`, so pulling the bottom edge up to
           * the visible band is what keeps a long list scrollable on screen
           * instead of running under the fold. Only the bottom edge moves: the
           * paper's `top` is written by MUI in viewport coordinates measured from
           * this box's top, so that edge has to stay where it is.
           * `useOverlayViewportClamp` handles the shift for lists that still
           * open past the band.
           */
          root: {
            bottom: visibleViewport.bottom,
          },
          paper: ({ theme }) => ({
            /*
             * Where the paper sits before MUI positions it. `MenuList` focuses
             * its first item in a layout effect, before Popover writes the real
             * `top`, so that focus lands on a paper still at the frame's top
             * edge. In a host frame taller than the area it shows, that edge is
             * scrolled out of view and the browser scrolls the host up to reveal
             * the item — opening a low Select jumped the whole hub page to the
             * top. Starting inside the visible band keeps the focused item on
             * screen; MUI's inline `top` replaces this before paint. The 16px
             * covers the band lagging a host scroll by up to one marker spacing.
             */
            top: `calc(${visibleViewport.top} + ${theme.spacing(2)})`,
            marginTop: surface.popoverGap,
            // A dropdown list reads as part of its control, so it takes the
            // control radius rather than the surface default.
            borderRadius: radius.sm,
          }),
        },
      },
      // Date/time pickers use their own Popper. It exposes the resolved side as
      // `data-popper-placement`, so the gap can follow the panel when it flips
      // above the field.
      MuiPickerPopper: {
        styleOverrides: {
          paper: {
            '[data-popper-placement^="bottom"] &': {
              marginTop: surface.popoverGap,
            },
            '[data-popper-placement^="top"] &': {
              marginBottom: surface.popoverGap,
            },
          },
        },
      },
      // Autocomplete positions with Popper, so the gap goes through a
      // placement-aware offset modifier: it stays on the correct side when the
      // list flips above the input.
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: radius.sm,
          },
          listbox: {
            paddingBlock: 0,
            /*
             * MUI's own cap is `40vh`, and `vh` measures the app's viewport
             * rather than the part of it a host frame shows. Capping against the
             * visible band as well keeps the list inside the screen; standalone
             * the second term is `100vh - 64px`, so `40vh` still wins.
             */
            maxHeight: `min(40vh, calc(${visibleViewport.height} - 64px))`,
          },
          popupIndicator: ({ theme }) => ({
            transition: theme.transitions.create("transform", {
              duration: motion.duration.feedback,
              easing: motion.easing.standard,
            }),
          }),
          popupIndicatorOpen: {
            transform: "rotate(180deg)",
          },
        },
        defaultProps: {
          popupIcon: createElement(ExpandMoreIcon),
          slotProps: {
            popper: {
              modifiers: [{ name: "offset", options: { offset: [0, 4] } }],
            },
          },
        },
      },
    },
  },
  /*
   * MUI X and MUI core UI strings. adapterLocale in App.tsx only localizes date
   * formatting; these are the component labels, which otherwise fall back to
   * enUS. Applied on the theme so LocalizationProvider picks them up as its
   * lowest-priority defaults, leaving the localeText prop free for overrides.
   */
  pickersPtPT,
  corePtPT,
);

export default theme;
